import { org_sector_type_enum } from '@predium/enums';
import { PreTFunction } from '@predium/i18n/client';
import { PATHS } from '@/routes';

export type BreadcrumbNode = {
  label: string;
  href?: string;
  /**
   * When true, the walker advances over this segment without pushing a
   * breadcrumb entry — used for dynamic id placeholders that shouldn't
   * appear as their own crumb. The walker also bubbles the segment's path
   * into the previous entry's href so the parent link points at the full
   * dynamic URL.
   */
  skip?: boolean;
  children?: Record<string, BreadcrumbNode>;
};

// Extracts the last URL segment, ignoring query strings.
const seg = (path: string) => path.split('/').filter(Boolean).at(-1) ?? '';

export function getBreadcrumbConfig(t: PreTFunction, orgSector: org_sector_type_enum): Record<string, BreadcrumbNode> {
  return {
    [seg(PATHS.statusQuoAnalysis.root())]: {
      label: t('General_StatusQuoAnalysis'),
      children: {
        [seg(PATHS.statusQuoAnalysis.buildings())]: {
          label: t('General_Building', { count: 2 }),
        },
        [seg(PATHS.statusQuoAnalysis.portfolio())]: {
          label: t('General_Portfolio', { count: 2 }),
        },
        // Sector-dependent segments — PATHS picks one URL segment at runtime so both are kept as literals.
        'economic-unit': {
          label: t('General_EconomicUnit', { orgSector: org_sector_type_enum.STANDARD, count: 2 }),
        },
        transaction: {
          label: t('General_EconomicUnit', { orgSector: org_sector_type_enum.BANKING, count: 2 }),
        },
      },
    },
    [seg(PATHS.actionPlanning.root())]: {
      label: t('General_ActionPlanning'),
      children: {
        [seg(PATHS.actionPlanning.scenarios())]: {
          label: t('PageName_Scenarios'),
          href: PATHS.actionPlanning.scenarios(),
          children: {
            [seg(PATHS.actionPlanning.scenario({ scenarioId: Number() }))]: {
              label: t('PageName_Scenario'),
              children: {
                'action-plans': { label: t('General_ActionPlan', { count: 1 }) },
              },
            },
          },
        },
      },
    },
    [seg(PATHS.reporting.root())]: {
      label: t('General_Reporting'),
      children: {
        [seg(PATHS.reporting.reports())]: {
          label: t('Reporting_Report', { count: 2 }),
          href: PATHS.reporting.reports(),
          children: {
            [seg(PATHS.reporting.report({ id: Number() }))]: {
              label: t('Reporting_Report', { count: 1 }),
              href: PATHS.reporting.report({ id: Number() }),
            },
          },
        },
      },
    },
    [seg(PATHS.dataCollection.root())]: {
      label: t('General_DataCollection'),
      children: {
        [seg(PATHS.dataCollection.buildings())]: {
          label: t('General_Building', { count: 2 }),
          href: PATHS.dataCollection.buildings(),
          children: {
            [seg(PATHS.dataCollection.building({ id: Number() }))]: {
              label: t('General_Building', { count: 1 }),
              href: PATHS.dataCollection.building({ id: Number() }),
            },
          },
        },
        // DC always uses 'economic-units' segment regardless of sector — label is sector-aware.
        [seg(PATHS.dataCollection.economicUnits())]: {
          label: t('General_EconomicUnit', { count: 2, orgSector }),
          href: PATHS.dataCollection.economicUnits(),
        },
        [seg(PATHS.dataCollection.portfolios())]: {
          label: t('General_Portfolio', { count: 2 }),
          href: PATHS.dataCollection.portfolios(),
        },
        [seg(PATHS.dataCollection.energySuppliers())]: {
          label: t('DataCollection_EnergySupplier', { count: 2 }),
          href: PATHS.dataCollection.energySuppliers(),
        },
        [seg(PATHS.dataCollection.consumptionHub())]: {
          label: t('General_ConsumptionHub'),
          href: PATHS.dataCollection.consumptionHub(),
        },
        [seg(PATHS.dataCollection.consumptionInvoices())]: {
          label: t('General_ConsumptionInvoices'),
          href: PATHS.dataCollection.consumptionInvoices(),
        },
        [seg(PATHS.dataCollection.consumptionDataHub())]: {
          label: t('PageName_ConsumptionDataHub'),
        },
      },
    },
    [seg(PATHS.drive.root())]: {
      label: t('General_Drive'),
    },
    [seg(PATHS.org.root())]: {
      label: t('General_Admin'),
      href: PATHS.org.root(),
      children: {
        [seg(PATHS.org.users())]: {
          label: t('PageName_ManageUsers'),
          href: PATHS.org.users(),
          children: {
            [seg(PATHS.org.user({ id: Number() }))]: {
              label: t('General_User'),
              href: PATHS.org.user({ id: Number() }),
            },
          },
        },
        [seg(PATHS.org.settings())]: {
          label: t('Settings_ManageOrganization'),
          href: PATHS.org.settings(),
          children: {
            // Connectors live under Org > Settings. The chain reads
            // "Administrator > Organisation verwalten > Konnektor" on
            // /org/settings/connectors/{id} and
            // "... > Konnektor > Zähler" on the deeper meter URL.
            //
            // Dynamic id segments (the {cId} and {mId} placeholders) are
            // marked `skip: true` so they don't appear as their own crumb —
            // the walker bubbles their path up to the previous entry's href
            // (so "Konnektor" on a meter URL links back to the connector
            // detail page). The walker also tags the last entry as current,
            // so "Konnektor" on a connector URL and "Zähler" on a meter URL
            // both render grey instead of as clickable links.
            [seg(PATHS.connectors.root())]: {
              label: t('Connectors_Platform'),
              children: {
                [seg(PATHS.connectors.detail('0'))]: {
                  label: '',
                  skip: true,
                  children: {
                    meters: {
                      label: t('Connectors_Meter'),
                      children: {
                        [seg(PATHS.connectors.meter('0', '0'))]: {
                          label: '',
                          skip: true,
                        },
                      },
                    },
                  },
                },
              },
            },
            // Standard energy prices live under Org > Settings too, so the chain reads
            // "Administrator > Organisation verwalten > Standard-Energiepreise".
            [seg(PATHS.org.standardEnergyPrices())]: {
              label: t('OrgSettings_StandardEnergyPrices_Title'),
            },
          },
        },
      },
    },
  };
}
